import { AbstractAlgorithm, AlgorithmInput, AlgorithmOutput, Cluster } from '@googlemaps/markerclusterer';
import SuperCluster, { ClusterFeature } from 'supercluster';
import equal from 'fast-deep-equal';

export type SuperClusterOptions = SuperCluster.Options<
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  { [name: string]: any },
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  { [name: string]: any }
>;

/**
 * A very fast JavaScript algorithm for geospatial point clustering using KD trees.
 *
 * @see https://www.npmjs.com/package/supercluster for more information on options.
 */

export class BoundingSuperClusterAlgorithm extends AbstractAlgorithm {
  protected superCluster: SuperCluster;
  protected markers: google.maps.Marker[];
  protected clusters: Cluster[];
  protected state: { zoom: number; boundingBox: [number, number, number, number] };

  constructor({ maxZoom, radius = 60, ...options }: SuperClusterOptions) {
    super({ maxZoom });

    this.superCluster = new SuperCluster({
      maxZoom: this.maxZoom,
      radius,
      ...options
    });

    this.state = { zoom: null, boundingBox: [-180, -90, 180, 90] };
  }

  public calculate(input: AlgorithmInput): AlgorithmOutput {
    let changed = false;

    if (!equal(input.markers, this.markers)) {
      changed = true;
      this.markers = [...input.markers] as google.maps.Marker[];

      // account for markers that have valid geojson but has empty or invalid coordinates array
      const points = this.markers.reduce((acc, marker) => {
        const position = marker.getPosition();

        if (!position) {
          return acc;
        }

        const lat = position.lat();
        const lng = position.lng();

        if (typeof lat === 'number' && typeof lng === 'number') {
          const point = {
            type: 'Feature' as const,
            geometry: {
              type: 'Point' as const,
              coordinates: [lng, lat]
            },
            properties: { marker }
          };

          acc.push(point);
        }

        return acc;
      }, []);

      this.superCluster.load(points);
    }

    const state = { zoom: input.map.getZoom(), boundingBox: this.getBoundingBox(input.map) };

    if (!changed && !equal(this.state, state)) {
      changed = true;
    }

    this.state = state;

    if (changed) {
      this.clusters = this.cluster(input);
    }

    return { clusters: this.clusters, changed };
  }

  public cluster({ map }: AlgorithmInput): Cluster[] {
    return this.superCluster.getClusters(this.getBoundingBox(map), Math.round(map.getZoom())).map(this.transformCluster.bind(this));
  }

  protected getBoundingBox(map: google.maps.Map): [number, number, number, number] {
    const bounds = map.getBounds().toJSON();
    return [bounds.west, bounds.south, bounds.east, bounds.north];
  }

  protected transformCluster({
    geometry: {
      coordinates: [lng, lat]
    },
    properties
  }: ClusterFeature<{ marker: google.maps.Marker }>): Cluster {
    if (properties.cluster) {
      return new Cluster({
        markers: this.superCluster.getLeaves(properties.cluster_id, Infinity).map(leaf => leaf.properties.marker),
        position: new google.maps.LatLng({ lat, lng })
      });
    } else {
      const marker = properties.marker;

      return new Cluster({
        markers: [marker],
        position: marker.getPosition()
      });
    }
  }
}
